import { useEffect, useState } from "react";
import type { Task } from "../api/types";
import { ChevronDown, ChevronRight, Play, Pause, Pencil, Trash2, Loader2 } from "lucide-react";
import { TaskDetails } from "./TaskDetails";
import { useTaskStream } from "../hooks/useTaskStream";

type Props = {
  task: Task;
  archived?: boolean;
  onStart: () => Promise<void>;
  onStop: () => Promise<void>;
  onEdit: () => void;
  onDelete: () => Promise<void>;
  onFdvChange?: (taskId: string, fdvUsd: number | null) => void;
};

function formatUsdBound(value: number): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    return `$${millions.toLocaleString("en-US", {
      minimumFractionDigits: millions % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 2,
    })}M`;
  }
  if (value >= 1_000) {
    const thousands = value / 1_000;
    return `$${thousands.toLocaleString("en-US", {
      minimumFractionDigits: thousands % 1 === 0 ? 0 : 1,
      maximumFractionDigits: 1,
    })}k`;
  }
  return `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export function TaskRow({ task, archived = false, onStart, onStop, onEdit, onDelete, onFdvChange }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const streamEnabled = task.status === "running" || expanded;
  const { stats, tradeTotals: liveTotals, executions } = useTaskStream(streamEnabled ? task.id : null);
  const tradeTotals = liveTotals ?? task.tradeTotals;

  useEffect(() => {
    if (stats) onFdvChange?.(task.id, stats.fdvUsd);
  }, [onFdvChange, stats, task.id]);

  async function wrap(fn: () => Promise<void>): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }

  const unusableTokenWallets =
    stats?.wallets.filter((w) =>
      w.tokenUsdValue !== null
        ? w.tokenUsdValue < 0.01
        : w.tokenUiAmount <= 0,
    ).length ?? 0;
  const liveWallets = stats?.wallets.length ?? 0;
  const tokenWarning =
    task.status === "running" &&
    task.action === "sell" &&
    liveWallets > 0 &&
    unusableTokenWallets / liveWallets > 0.4;
  const statusColor = tokenWarning
    ? "bg-warn animate-pulse"
    : task.status === "running"
      ? "bg-ok"
      : task.status === "error"
        ? "bg-err"
        : "bg-muted";
  const statusTitle = tokenWarning
    ? `${unusableTokenWallets}/${liveWallets} wallets have less than $0.01 of target-token inventory`
    : task.status;

  const mcStr = task.config.mcRangeUsd
    ? `${formatUsdBound(task.config.mcRangeUsd[0])} - ${formatUsdBound(task.config.mcRangeUsd[1])}`
    : "any MC";
  const triggerStr =
    task.config.minTriggerUsd !== undefined && task.config.maxTriggerUsd !== undefined
      ? `${formatUsdBound(task.config.minTriggerUsd)}–${formatUsdBound(task.config.maxTriggerUsd)}`
      : task.config.minTriggerUsd !== undefined
        ? `${formatUsdBound(task.config.minTriggerUsd)}+`
        : task.config.maxTriggerUsd !== undefined
          ? `≤${formatUsdBound(task.config.maxTriggerUsd)}`
          : "any trigger";

  return (
    <div className="border border-border hover:border-fg/30 transition-colors">
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-4 cursor-pointer select-none sm:px-6"
        onClick={() => setExpanded((e) => !e)}
      >
        <button className="text-muted hover:text-fg" onClick={(e) => e.stopPropagation()}>
          {expanded ? (
            <ChevronDown size={16} strokeWidth={1.5} onClick={() => setExpanded(false)} />
          ) : (
            <ChevronRight size={16} strokeWidth={1.5} onClick={() => setExpanded(true)} />
          )}
        </button>
        <div className={`w-2 h-2 rounded-full ${statusColor}`} title={statusTitle} />
        <div className="min-w-0 flex-1 grid grid-cols-2 items-center gap-x-4 gap-y-3 text-sm sm:grid-cols-4 xl:grid-cols-7">
          <div className="min-w-0">
            <div className="font-medium break-words">{task.name}</div>
            <div className="text-[11px] font-mono text-muted">
              {task.action === "buy" ? "react to sells" : "react to buys"}
            </div>
          </div>
          <div>
            <span
              className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-nav border ${
                task.action === "buy" ? "border-ok text-ok" : "border-err text-err"
              }`}
            >
              {task.action}
            </span>
          </div>
          <div className="font-mono text-xs">
            {mcStr}
          </div>
          <div className="font-mono text-xs">
            {triggerStr}
          </div>
          <div className="font-mono text-xs">
            {task.config.counterSizePctRange[0]}–{task.config.counterSizePctRange[1]}%
          </div>
          <div className="font-mono text-xs text-muted">
            {task.walletCount} wallet{task.walletCount === 1 ? "" : "s"}
          </div>
          <div className="font-mono text-[11px]">
            {task.status === "error" ? (
              <span className="text-err" title={task.lastError ?? ""}>
                error
              </span>
            ) : (
              <span className="text-muted">{task.status}</span>
            )}
          </div>
          <div className="col-span-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] sm:col-span-4 xl:col-span-7">
            <span className="text-muted">
              Our buys <span className="text-ok">${tradeTotals.buyUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </span>
            <span className="text-muted">
              Our sells <span className="text-err">{tradeTotals.estimatedSellCount > 0 ? "≈" : ""}${tradeTotals.sellUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </span>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {task.status === "running" ? (
            <button
              className="btn btn-ghost px-2 py-1 text-[10px]"
              onClick={() => void wrap(onStop)}
              disabled={busy}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Pause size={12} strokeWidth={1.5} />}
              Stop
            </button>
          ) : (
            <button
              className="btn btn-primary px-2 py-1 text-[10px]"
              onClick={() => void wrap(onStart)}
              disabled={busy || archived}
              title={archived ? "Restore the client before starting this task" : undefined}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} strokeWidth={1.5} />}
              Start
            </button>
          )}
          <button
            className="btn btn-ghost px-2 py-1 text-[10px]"
            onClick={onEdit}
            disabled={task.status === "running" || archived}
            title={archived ? "Restore the client before editing" : task.status === "running" ? "Stop task to edit" : undefined}
          >
            <Pencil size={12} strokeWidth={1.5} />
          </button>
          <button
            className="btn btn-ghost px-2 py-1 text-[10px] text-err"
            onClick={() => void wrap(onDelete)}
            disabled={busy}
          >
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {expanded && <TaskDetails task={task} stats={stats} tradeTotals={tradeTotals} executions={executions} />}
    </div>
  );
}
